import { MAINTENANCE_MESSAGE_EN } from "../i18n/maintenanceMessages";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { formatApiError } from "../utils/apiErrors";
import { useAuth } from "../context/AuthContext";
import { metenApi } from "../services/metenApi";
import {
  buildManagedUserProfilePayload,
  buildManagedUserDemoPayload,
  buildManagedUserAccessPayload,
  normalizeAdminFlag,
  normalizeAdminManagedUser,
} from "../utils/adminUsers";
import { useIsGreek } from "../context/LanguageContext";
import { userEditorText, editorSectionSx, editorFieldsSx, editorSwitchesSx } from "./adminUserEditorPresentation";
import AppDatePicker from "../components/common/AppDatePicker";
import AdminUserMonitoring from "./AdminUserMonitoring";
import AdminAnnouncements from "./AdminAnnouncements";

function formatUserName(user) {
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
  return fullName || user?.displayName || "-";
}

// One formatter for the whole app: this page used to print the browser's raw
// "Failed to fetch" straight into the alert.
function formatErrorMessage(err) {
  if (!err) return "Unexpected error.";
  return formatApiError(err, "Unexpected error.");
}

function formatDemoDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function formatDemoStartInputValue(value) {
  const startedAt = formatDemoDate(value);
  if (!startedAt) return "";
  const year = startedAt.getFullYear();
  const month = String(startedAt.getMonth() + 1).padStart(2, "0");
  const day = String(startedAt.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDemoExpiryCell(user) {
  const activeDays = Number(user?.demoActiveDays) || 0;
  const startedAt = formatDemoDate(user?.demoStartedAt);
  if (!startedAt) return user?.demoModeEnabled ? "Missing" : "-";

  const expiryDate = new Date(startedAt);
  expiryDate.setHours(0, 0, 0, 0);
  expiryDate.setDate(expiryDate.getDate() + activeDays);
  return expiryDate.toLocaleDateString("en-GB");
}

function formatCreatedDate(value) {
  const createdAt = formatDemoDate(value);
  if (!createdAt) return "-";
  return createdAt.toLocaleDateString("en-GB");
}

function formatDateTime(value) {
  const parsed = formatDemoDate(value);
  if (!parsed) return null;
  const datePart = parsed.toLocaleDateString("en-GB");
  const timePart = parsed.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${datePart} ${timePart}`;
}

const ACCESS_LABELS = {
  regular_market_operations_enabled: "Market Operations",
  regular_can_submit_bids: "Bid submissions",
  regular_wind_system_enabled: "Wind System",
  regular_market_data_enabled: "Market Data",
  regular_feasibility_enabled: "Feasibility",
  regular_merchant_enabled: "Merchant",
  regular_ppa_enabled: "PPA",
  regular_tolling_enabled: "Tolling",
  regular_monitoring_enabled: "Asset Monitoring",
  regular_multiple_assets_enabled: "Multiple assets per project",
  regular_market_optimization_limit_per_day: "Market runs/day",
  regular_feasibility_limit_per_day: "Feasibility runs/day",
  regular_max_projects: "Maximum projects",
  regular_max_simulation_days: "Maximum simulation days",
};

function describeAccessChange(change, text) {
  const before = change?.previous_access || {};
  const after = change?.new_access || {};
  return Object.keys(ACCESS_LABELS)
    .filter((key) => before[key] !== after[key])
    .map((key) => {
      const value = after[key];
      const display = value == null ? text("unlimited")
        : typeof value === "boolean" ? text(value ? "Enabled" : "Disabled") : String(value);
      return `${text(ACCESS_LABELS[key])}: ${display}`;
    })
    .join("; ") || text("No effective permission change");
}

function createEditDraft(user) {
  return {
    ...user,
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    organization: user?.organization || "",
    phone: user?.phone || "",
    demoStartedAt: user?.demoStartedAt || "",
    demoActiveDays: Number(user?.demoActiveDays) || 0,
    demoRemainingDays: Number(user?.demoRemainingDays) || 0,
    demoMarketOptimizationLimitPerDay: Number(user?.demoMarketOptimizationLimitPerDay) || 0,
    demoFeasibilityLimitPerDay: Number(user?.demoFeasibilityLimitPerDay) || 0,
    demoCanSubmitBids: Boolean(user?.demoCanSubmitBids),
    demoMarketOperationsEnabled: Boolean(user?.demoMarketOperationsEnabled),
    demoWindSystemEnabled: Boolean(user?.demoWindSystemEnabled),
    demoMarketDataEnabled: Boolean(user?.demoMarketDataEnabled),
    demoMerchantEnabled: Boolean(user?.demoMerchantEnabled),
    demoPpaEnabled: Boolean(user?.demoPpaEnabled),
    demoTollingEnabled: Boolean(user?.demoTollingEnabled),
    demoMultipleAssetsEnabled: Boolean(user?.demoMultipleAssetsEnabled),
    regularMarketOptimizationLimitPerDay: user?.regularMarketOptimizationLimitPerDay ?? "",
    regularFeasibilityLimitPerDay: user?.regularFeasibilityLimitPerDay ?? "",
    regularMaxProjects: user?.regularMaxProjects ?? "",
    regularMaxSimulationDays: user?.regularMaxSimulationDays ?? "",
  };
}

function normalizeDraftValue(field, value, currentValue) {
  if (
    field === "demoModeEnabled" ||
    field === "demoLocked" ||
    field === "demoCanSubmitBids" ||
    field === "demoMarketOperationsEnabled" ||
    field === "demoWindSystemEnabled" ||
    field === "demoMarketDataEnabled"
    || field === "demoMerchantEnabled"
    || field === "demoPpaEnabled"
    || field === "demoTollingEnabled"
    || field === "demoMultipleAssetsEnabled"
    || field === "regularMarketOperationsEnabled"
    || field === "regularCanSubmitBids"
    || field === "regularWindSystemEnabled"
    || field === "regularMarketDataEnabled"
    || field === "regularFeasibilityEnabled"
    || field === "regularMerchantEnabled"
    || field === "regularPpaEnabled"
    || field === "regularTollingEnabled"
    || field === "regularMonitoringEnabled"
    || field === "regularMultipleAssetsEnabled"
  )
    return Boolean(value);
  if (field === "demoStartedAt") return value;
  if (field === "firstName" || field === "lastName" || field === "organization" || field === "phone") {
    return value;
  }
  if (value === "") return "";
  if (!Number.isFinite(Number(value))) return currentValue;
  return Math.max(0, Number(value));
}

function DemoStatusChip({ user }) {
  if (!user?.demoModeEnabled) {
    if (user?.role === "admin") return <Chip size="small" label="Admin" color="primary" />;
    const restricted = !user?.regularMarketOperationsEnabled || !user?.regularCanSubmitBids ||
      !user?.regularWindSystemEnabled || !user?.regularMarketDataEnabled ||
      !user?.regularFeasibilityEnabled || !user?.regularMerchantEnabled || !user?.regularPpaEnabled ||
      !user?.regularTollingEnabled || !user?.regularMonitoringEnabled ||
      !user?.regularMultipleAssetsEnabled ||
      user?.regularMarketOptimizationLimitPerDay != null ||
      user?.regularFeasibilityLimitPerDay != null || user?.regularMaxProjects != null ||
      user?.regularMaxSimulationDays != null;
    return <Chip size="small" label={restricted ? "Restricted" : "Standard"}
      color={restricted ? "warning" : "primary"} />;
  }

  return (
    <Chip
      size="small"
      label={user?.demoLocked ? "Demo Locked" : "Demo"}
      color="warning"
    />
  );
}

export default function AdminUsers() {
  const isGreek = useIsGreek();
  const editorText = (text) => userEditorText(text, isGreek);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [persistedUsers, setPersistedUsers] = useState({});
  const [filters, setFilters] = useState({
    id: "",
    name: "",
    email: "",
  });
  const [savingUserId, setSavingUserId] = useState("");
  const [editingUserId, setEditingUserId] = useState("");
  const [editDraft, setEditDraft] = useState(null);
  const [accessHistory, setAccessHistory] = useState([]);
  const [adminTab, setAdminTab] = useState("users");
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
    icon: null,
  });
  const [maintenanceOn, setMaintenanceOn] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);

  const handleBack = useCallback(() => {
    const returnTo = location.state?.accountReturnTo;
    const destination = typeof returnTo === "string" &&
      returnTo.startsWith("/") && !returnTo.startsWith("//") &&
      !returnTo.startsWith("/admin/users")
      ? returnTo
      : "/project/overview";
    navigate(destination, { state: { openAccountProfile: true } });
  }, [location.state, navigate]);

  const showSnack = useCallback((message, severity = "success") => {
    setSnack({
      open: true,
      message,
      severity,
      icon:
        severity === "success" ? (
          <CheckCircleOutlineIcon fontSize="small" />
        ) : (
          <ErrorOutlineOutlinedIcon fontSize="small" />
        ),
    });
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const status = await metenApi.getSystemStatus();
        if (active) setMaintenanceOn(Boolean(status?.maintenance_mode));
      } catch (_) {
        // leave default (off) on error
      } finally {
        if (active) setMaintenanceLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleToggleMaintenance = useCallback(
    async (next) => {
      setMaintenanceSaving(true);
      try {
        const status = await metenApi.setMaintenanceMode(
          next,
          next
            ? MAINTENANCE_MESSAGE_EN
            : null
        );
        setMaintenanceOn(Boolean(status?.maintenance_mode));
        showSnack(
          next
            ? (isGreek ? "Η συντήρηση ενεργοποιήθηκε. Η προβολή και η αποθήκευση υπάρχοντος έργου παραμένουν διαθέσιμες." : "Maintenance is on. Viewing data and saving existing projects remain available.")
            : (isGreek ? "Η συντήρηση απενεργοποιήθηκε." : "Maintenance mode OFF."),
          "success"
        );
      } catch (err) {
        showSnack(`${isGreek ? "Δεν ήταν δυνατή η αλλαγή της λειτουργίας συντήρησης" : "Failed to update maintenance mode"}: ${err?.message || err}`, "error");
      } finally {
        setMaintenanceSaving(false);
      }
    },
    [showSnack, isGreek]
  );

  useEffect(() => {
    let active = true;
    setProfileLoading(true);
    (async () => {
      try {
        const payload = await metenApi.getProfile();
        if (!active) return;
        setProfile(payload || null);
      } catch {
        if (!active) return;
        setProfile(null);
      } finally {
        if (active) setProfileLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const isAdminUser = useMemo(() => normalizeAdminFlag(profile, user), [profile, user]);

  useEffect(() => {
    if (profileLoading || !isAdminUser) return undefined;
    let active = true;
    setUsersLoading(true);
    setUsersError("");
    (async () => {
      try {
        const payload = await metenApi.listUsers();
        if (!active) return;
        const rows = Array.isArray(payload) ? payload : payload?.users || [];
        const normalizedRows = rows.map((row) => normalizeAdminManagedUser(row));
        setUsers(normalizedRows);
        setPersistedUsers(
          Object.fromEntries(normalizedRows.map((managedUser) => [String(managedUser.id), managedUser]))
        );
      } catch (err) {
        if (!active) return;
        setUsers([]);
        setPersistedUsers({});
        setUsersError(formatErrorMessage(err));
      } finally {
        if (active) setUsersLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [isAdminUser, profileLoading]);

  const filteredUsers = useMemo(() => {
    const normalizedFilters = {
      id: filters.id.trim().toLowerCase(),
      name: filters.name.trim().toLowerCase(),
      email: filters.email.trim().toLowerCase(),
    };

    return users.filter((managedUser) => {
      if (
        normalizedFilters.id &&
        !String(managedUser.id || "")
          .toLowerCase()
          .includes(normalizedFilters.id)
      ) {
        return false;
      }

      if (
        normalizedFilters.name &&
        !formatUserName(managedUser).toLowerCase().includes(normalizedFilters.name)
      ) {
        return false;
      }

      if (
        normalizedFilters.email &&
        !String(managedUser.email || "")
          .toLowerCase()
          .includes(normalizedFilters.email)
      ) {
        return false;
      }

      return true;
    });
  }, [filters, users]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleOpenEditor = useCallback((managedUser) => {
    setEditingUserId(String(managedUser?.id || ""));
    setEditDraft(createEditDraft(managedUser));
    setAccessHistory([]);
    if (!managedUser?.demoModeEnabled && managedUser?.role !== "admin") {
      metenApi.getUserAccessHistory(managedUser.id)
        .then((payload) => setAccessHistory(payload?.changes || []))
        .catch(() => setAccessHistory([]));
    }
  }, []);

  const handleCloseEditor = useCallback(() => {
    if (savingUserId) return;
    setEditingUserId("");
    setEditDraft(null);
  }, [savingUserId]);

  const handleDraftChange = useCallback((field, value) => {
    setEditDraft((prev) =>
      prev
        ? {
            ...prev,
            [field]: normalizeDraftValue(field, value, prev[field]),
          }
        : prev
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (!editDraft?.id) return;
    setSavingUserId(String(editDraft.id));
    try {
      const persistedUser = persistedUsers[String(editDraft.id)] || {};
      const profilePayload = buildManagedUserProfilePayload(editDraft, persistedUser);
      const profileUpdate = Object.keys(profilePayload).length
        ? (await metenApi.updateUserProfile(editDraft.id, profilePayload)) || {}
        : {};
      const demoUpdate =
        (await metenApi.updateUserDemoSettings(editDraft.id, buildManagedUserDemoPayload(editDraft))) ||
        {};
      const accessPayload = buildManagedUserAccessPayload(editDraft, persistedUser);
      const accessUpdate = !editDraft.demoModeEnabled && editDraft.role !== "admin" &&
        Object.keys(accessPayload).length
        ? (await metenApi.updateUserAccess(editDraft.id, accessPayload)) || {}
        : {};
      const normalized = normalizeAdminManagedUser({
        ...persistedUser,
        ...editDraft,
        ...profileUpdate,
        ...demoUpdate,
        ...accessUpdate,
      });
      setUsers((prev) => prev.map((item) => (item.id === editDraft.id ? normalized : item)));
      setPersistedUsers((prev) => ({ ...prev, [String(editDraft.id)]: normalized }));
      setEditDraft(createEditDraft(normalized));
      setEditingUserId("");
      setEditDraft(null);
      showSnack(`Saved user settings for ${normalized.email || normalized.id}.`);
    } catch (err) {
      showSnack(formatErrorMessage(err), "error");
    } finally {
      setSavingUserId("");
    }
  }, [editDraft, persistedUsers, showSnack]);

  if (profileLoading) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography color="text.secondary">Loading user management...</Typography>
      </Box>
    );
  }

  if (!isAdminUser) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, maxWidth: 720 }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              User management is admin-only
            </Typography>
            <Typography color="text.secondary">
              Your current account does not have permission to view the full users list.
            </Typography>
            <Box>
              <Button variant="contained" onClick={handleBack}>
                Back to account
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        p: { xs: 2, md: 3.5 },
        display: "flex",
        flexDirection: "column",
        gap: 2,
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(180deg, #0f172a 0%, #111827 100%)"
            : "linear-gradient(180deg, #edf5ff 0%, #f8fbff 22%, #ffffff 100%)",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 2.5 },
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(180deg, rgba(15,23,42,0.88) 0%, rgba(15,23,42,0.72) 100%)"
              : "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={handleBack}
            >
              Back
            </Button>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "0.01em" }}>
                User Management
              </Typography>
              <Typography color="text.secondary">
                Only admins have access for an overview of all users.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                color="warning"
                size="small"
                checked={maintenanceOn}
                disabled={maintenanceLoading || maintenanceSaving}
                onChange={(e) => handleToggleMaintenance(e.target.checked)}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: maintenanceOn ? "warning.main" : "text.secondary" }}
              >
                Maintenance mode{maintenanceOn ? " (ON)" : ""}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Tabs value={adminTab} onChange={(_, value) => setAdminTab(value)}
        aria-label={isGreek ? "Διαχείριση χρηστών" : "User management"}>
        <Tab value="users" label={isGreek ? "Χρήστες" : "Users"} id="admin-users-tab" aria-controls="admin-users-panel" />
        <Tab value="monitoring" label={isGreek ? "Παρακολούθηση" : "Monitoring"} id="admin-monitoring-tab" aria-controls="admin-monitoring-panel" />
        <Tab value="announcements" label={isGreek ? "Ανακοινώσεις" : "Announcements"} id="admin-announcements-tab" aria-controls="admin-announcements-panel" />
      </Tabs>
      {adminTab === "monitoring" ? (
        <Box role="tabpanel" id="admin-monitoring-panel" aria-labelledby="admin-monitoring-tab" sx={{ minWidth: 0 }}>
          <AdminUserMonitoring />
        </Box>
      ) : adminTab === "announcements" ? (
        <Box role="tabpanel" id="admin-announcements-panel" aria-labelledby="admin-announcements-tab" sx={{ minWidth: 0 }}>
          <AdminAnnouncements />
        </Box>
      ) : <Paper
        role="tabpanel" id="admin-users-panel" aria-labelledby="admin-users-tab"
        variant="outlined"
        sx={{
          borderRadius: 3,
          p: { xs: 1.25, md: 2 },
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "rgba(15,23,42,0.52)" : "rgba(255,255,255,0.92)",
        }}
      >
        <Stack spacing={1.5} sx={{ mb: 1.5 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Stack spacing={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Users List
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredUsers.length} of {users.length} users shown.
              </Typography>
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
            <TextField
              size="small"
              value={filters.id}
              onChange={(e) => handleFilterChange("id", e.target.value)}
              placeholder="Filter ID"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: "100%", md: 180 } }}
            />
            <TextField
              size="small"
              value={filters.name}
              onChange={(e) => handleFilterChange("name", e.target.value)}
              placeholder="Filter name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: "100%", md: 220 } }}
            />
            <TextField
              size="small"
              value={filters.email}
              onChange={(e) => handleFilterChange("email", e.target.value)}
              placeholder="Filter email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: "100%", md: 240 } }}
            />
          </Stack>
        </Stack>

        {usersError ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {usersError}
          </Alert>
        ) : null}
        {usersLoading ? <Typography color="text.secondary">Loading users...</Typography> : null}

        {!usersLoading && users.length ? (
          <TableContainer
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              flex: 1,
              backgroundColor: "background.paper",
              "& .MuiTableCell-root": {
                borderBottomColor: "divider",
                px: 1.35,
                py: 0.34,
                fontSize: "1.05rem",
              },
              "& .MuiTableCell-head": {
                px: 1.2,
                py: 0.4,
                fontSize: "0.98rem",
              },
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 54, fontWeight: 800, color: "text.secondary" }}>#</TableCell>
                  <TableCell sx={{ minWidth: 130, fontWeight: 800 }}>User</TableCell>
                  <TableCell sx={{ minWidth: 110, fontWeight: 800 }}>Created</TableCell>
                  <TableCell sx={{ minWidth: 140, fontWeight: 800 }}>Last Sign-in</TableCell>
                  <TableCell sx={{ minWidth: 220, fontWeight: 800 }}>Contact</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 800 }}>Organization</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 800 }}>Platform Role</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 800 }}>Package</TableCell>
                  <TableCell sx={{ minWidth: 130, fontWeight: 800 }}>Last Release</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 800 }}>Remaining Today</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 800 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length ? (
                  filteredUsers.map((managedUser, index) => (
                    <TableRow
                      key={managedUser.id || managedUser.email}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.015)"
                              : "rgba(15,23,42,0.012)",
                        },
                      }}
                    >
                      <TableCell sx={{ color: "text.secondary", fontWeight: 700 }}>
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {formatUserName(managedUser)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {managedUser.id || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">{formatCreatedDate(managedUser.createdAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatDateTime(managedUser.lastSignInAt) || "Never"}
                        </Typography>
                        {formatDateTime(managedUser.lastActiveAt) ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            title={isGreek
                              ? "Η ένδειξη μπορεί να ενημερωθεί από αίτημα της εφαρμογής ή ανανέωση της συνεδρίας, χωρίς νέα είσοδο."
                              : "This can update after an app request or session refresh, without a new sign-in."}
                          >
                            {isGreek ? "Τελευταία ένδειξη συνεδρίας" : "Last session signal"}: {formatDateTime(managedUser.lastActiveAt)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {managedUser.email || "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {managedUser.phone || "No phone"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">{managedUser.organization || "-"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ textTransform: "capitalize" }}>
                          {managedUser.role || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <DemoStatusChip user={managedUser} />
                          {managedUser.demoModeEnabled ? (
                            <Typography variant="caption" color="text.secondary">
                              Expires {formatDemoExpiryCell(managedUser)}
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {managedUser.lastSeenPlatformVersion || "Not recorded"}
                        </Typography>
                        {managedUser.lastSeenPlatformVersionAt ? (
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(managedUser.lastSeenPlatformVersionAt)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Market {managedUser.demoModeEnabled
                            ? managedUser.marketOptimizationRemainingToday
                            : (managedUser.regularMarketOptimizationRemainingToday ?? "Unlimited")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Feasibility {managedUser.demoModeEnabled
                            ? managedUser.feasibilityRemainingToday
                            : (managedUser.regularFeasibilityRemainingToday ?? "Unlimited")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditOutlinedIcon />}
                          onClick={() => handleOpenEditor(managedUser)}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11}>
                      <Alert severity="info" sx={{ my: 1 }}>
                        No users match the current filter.
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </Paper>}

      <Dialog
        open={Boolean(editingUserId && editDraft)}
        onClose={handleCloseEditor}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3, m: { xs: 1, sm: 3 }, width: { xs: "calc(100% - 16px)", sm: "calc(100% - 48px)" } } }}
      >
        <DialogTitle sx={{ px: { xs: 2, sm: 3 }, py: 2, fontWeight: 700 }}>{editorText("Edit User")}</DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2.5 }, "& .MuiFormControl-root": { minWidth: 0 }, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}>
          {editDraft ? (
            <Stack spacing={2} sx={{ minWidth: 0 }}>
              <Box sx={{ px: 0.5, overflowWrap: "anywhere" }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {formatUserName(editDraft)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {editDraft.email || "-"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {editorText("User ID")}: {editDraft.id || "-"}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {editorText("Last release used")}: {editDraft.lastSeenPlatformVersion || editorText("Not recorded")}
                  {editDraft.lastSeenPlatformVersionAt
                    ? ` (${formatDateTime(editDraft.lastSeenPlatformVersionAt)})`
                    : ""}
                </Typography>
              </Box>

              <Stack spacing={2} sx={editorSectionSx}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {editorText("Profile")}
                </Typography>
                <Box sx={editorFieldsSx}>
                  <TextField
                    fullWidth
                    size="small"
                    label={editorText("First Name")}
                    value={editDraft.firstName || ""}
                    onChange={(e) => handleDraftChange("firstName", e.target.value)}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label={editorText("Last Name")}
                    value={editDraft.lastName || ""}
                    onChange={(e) => handleDraftChange("lastName", e.target.value)}
                  />
                </Box>
                <Box sx={editorFieldsSx}>
                  <TextField
                    fullWidth
                    size="small"
                    label={editorText("Organization")}
                    value={editDraft.organization || ""}
                    onChange={(e) => handleDraftChange("organization", e.target.value)}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label={editorText("Phone")}
                    value={editDraft.phone || ""}
                    onChange={(e) => handleDraftChange("phone", e.target.value)}
                  />
                </Box>
              </Stack>

              {!editDraft.demoModeEnabled && editDraft.role !== "admin" ? (
                <Stack spacing={2} sx={editorSectionSx}>
                  <Stack spacing={1.5} alignItems="flex-start">
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{editorText("Regular Account Access")}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {editorText("Empty limits mean unlimited.")}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ flexShrink: 0, "& .MuiButton-root": { textTransform: "none" } }}>
                      <Button size="small" variant="outlined" onClick={() => setEditDraft((prev) => ({
                        ...prev,
                        regularFeasibilityEnabled: true,
                        regularMarketOperationsEnabled: false,
                        regularCanSubmitBids: false,
                        regularWindSystemEnabled: true,
                        regularMarketDataEnabled: false,
                        regularMerchantEnabled: true,
                        regularPpaEnabled: false,
                        regularTollingEnabled: false,
                        regularMonitoringEnabled: false,
                        regularMultipleAssetsEnabled: false,
                        regularMarketOptimizationLimitPerDay: "",
                        regularFeasibilityLimitPerDay: "",
                        regularMaxProjects: "",
                        regularMaxSimulationDays: "",
                      }))}>{editorText("Feasibility only")}</Button>
                      <Button size="small" variant="outlined" onClick={() => setEditDraft((prev) => ({
                        ...prev,
                        regularFeasibilityEnabled: true,
                        regularMarketOperationsEnabled: true,
                        regularCanSubmitBids: true,
                        regularWindSystemEnabled: true,
                        regularMarketDataEnabled: true,
                        regularMerchantEnabled: true,
                        regularPpaEnabled: true,
                        regularTollingEnabled: true,
                        regularMonitoringEnabled: true,
                        regularMultipleAssetsEnabled: true,
                        regularMarketOptimizationLimitPerDay: "",
                        regularFeasibilityLimitPerDay: "",
                        regularMaxProjects: "",
                        regularMaxSimulationDays: "",
                      }))}>{editorText("Full access")}</Button>
                    </Stack>
                  </Stack>
                  <Box sx={editorSwitchesSx}>
                    {[
                      ["regularFeasibilityEnabled", "Feasibility"],
                      ["regularMarketOperationsEnabled", "Market Operations"],
                      ["regularCanSubmitBids", "Can submit bids"],
                      ["regularWindSystemEnabled", "Wind System"],
                      ["regularMarketDataEnabled", "Market Data"],
                      ["regularMerchantEnabled", "Merchant"],
                      ["regularPpaEnabled", "PPA"],
                      ["regularTollingEnabled", "Tolling models"],
                      ["regularMonitoringEnabled", "Asset Monitoring"],
                      ["regularMultipleAssetsEnabled", "Multiple assets per project (one active at a time)"],
                    ].map(([field, label]) => (
                      <Stack key={field} direction="row" spacing={0.5} alignItems="center">
                        <Switch size="small" checked={Boolean(editDraft[field])}
                          disabled={field === "regularCanSubmitBids" && !editDraft.regularMarketOperationsEnabled}
                          onChange={(e) => handleDraftChange(field, e.target.checked)} />
                        <Typography variant="body2">{editorText(label)}</Typography>
                      </Stack>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{editorText("Usage limits")}</Typography>
                  <Box sx={editorFieldsSx}>
                    {[
                      ["regularMarketOptimizationLimitPerDay", "Market runs / day"],
                      ["regularFeasibilityLimitPerDay", "Feasibility runs / day"],
                      ["regularMaxProjects", "Maximum projects"],
                      ["regularMaxSimulationDays", "Maximum simulation days"],
                    ].map(([field, label]) => (
                      <TextField key={field} size="small" type="number" label={editorText(label)}
                        value={editDraft[field]} inputProps={{ min: 0 }}
                        onChange={(e) => handleDraftChange(field, e.target.value)}
                        fullWidth InputLabelProps={{ shrink: true }} />
                    ))}
                  </Box>
                  {editDraft.accessUpdatedAt ? (
                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                      {editorText("Last changed")} {formatDateTime(editDraft.accessUpdatedAt)}
                      {editDraft.accessUpdatedBy ? ` ${editorText("by")} ${editDraft.accessUpdatedBy}` : ""}.
                    </Typography>
                  ) : null}
                  {accessHistory.length ? (
                    <Stack spacing={1} sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1.5, overflowWrap: "anywhere" }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>{editorText("Recent access changes")}</Typography>
                      {accessHistory.slice(0, 5).map((change, index) => (
                        <Typography key={`${change.changed_at}-${index}`} variant="caption" color="text.secondary">
                          <Box component="span" sx={{ display: "block", fontWeight: 600 }}>
                            {formatDateTime(change.changed_at)} {editorText("by")} {change.changed_by_email || change.changed_by || editorText("unknown admin")}
                          </Box>
                          {describeAccessChange(change, editorText)}
                        </Typography>
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              ) : null}

              <Stack spacing={2} sx={editorSectionSx}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {editorText("Demo Access")}
                </Typography>
                <Box sx={editorSwitchesSx}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      size="small"
                      checked={Boolean(editDraft.demoModeEnabled)}
                      onChange={(e) => handleDraftChange("demoModeEnabled", e.target.checked)}
                    />
                    <Typography variant="body2">{editorText("Demo mode enabled")}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      size="small"
                      checked={Boolean(editDraft.demoMarketOperationsEnabled)}
                      onChange={(e) =>
                        handleDraftChange("demoMarketOperationsEnabled", e.target.checked)
                      }
                      disabled={!editDraft.demoModeEnabled}
                    />
                    <Typography variant="body2">{editorText("Market Operations enabled")}</Typography>
                  </Stack>
                  {editDraft.demoMarketOperationsEnabled ? (
                    <Stack direction="row" spacing={1} alignItems="center" >
                      <Switch
                        size="small"
                        checked={Boolean(editDraft.demoCanSubmitBids)}
                        onChange={(e) => handleDraftChange("demoCanSubmitBids", e.target.checked)}
                        disabled={!editDraft.demoModeEnabled}
                      />
                      <Typography variant="body2">{editorText("Can submit bids")}</Typography>
                    </Stack>
                  ) : null}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      size="small"
                      checked={Boolean(editDraft.demoWindSystemEnabled)}
                      onChange={(e) =>
                        handleDraftChange("demoWindSystemEnabled", e.target.checked)
                      }
                      disabled={!editDraft.demoModeEnabled}
                    />
                    <Typography variant="body2">{editorText("Wind System enabled")}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      size="small"
                      checked={Boolean(editDraft.demoMarketDataEnabled)}
                      onChange={(e) =>
                        handleDraftChange("demoMarketDataEnabled", e.target.checked)
                      }
                      disabled={!editDraft.demoModeEnabled}
                    />
                    <Typography variant="body2">{editorText("Market Data enabled")}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      size="small"
                      checked={Boolean(editDraft.demoMerchantEnabled)}
                      onChange={(e) => handleDraftChange("demoMerchantEnabled", e.target.checked)}
                      disabled={!editDraft.demoModeEnabled}
                    />
                    <Typography variant="body2">{editorText("Merchant enabled")}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      size="small"
                      checked={Boolean(editDraft.demoPpaEnabled)}
                      onChange={(e) => handleDraftChange("demoPpaEnabled", e.target.checked)}
                      disabled={!editDraft.demoModeEnabled}
                    />
                    <Typography variant="body2">{editorText("PPA enabled")}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      size="small"
                      checked={Boolean(editDraft.demoTollingEnabled)}
                      onChange={(e) => handleDraftChange("demoTollingEnabled", e.target.checked)}
                      disabled={!editDraft.demoModeEnabled}
                    />
                    <Typography variant="body2">{editorText("Tolling models enabled")}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      size="small"
                      checked={Boolean(editDraft.demoMultipleAssetsEnabled)}
                      onChange={(e) => handleDraftChange("demoMultipleAssetsEnabled", e.target.checked)}
                      disabled={!editDraft.demoModeEnabled}
                    />
                    <Typography variant="body2">{editorText("Multiple assets per project (one active at a time)")}</Typography>
                  </Stack>
                  {editDraft.demoLocked ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" label={editorText("Demo locked")} color="warning" variant="outlined" />
                    </Stack>
                  ) : null}
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>{editorText("Duration and daily limits")}</Typography>
                <Box sx={editorFieldsSx}>
                  <AppDatePicker
                    label={editorText("Start Date")}
                    value={formatDemoStartInputValue(editDraft.demoStartedAt)}
                    onChange={(v) => handleDraftChange("demoStartedAt", v)}
                    sx={{ width: "100%", minWidth: 0 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    InputLabelProps={{ shrink: true }} label={editorText("Configured Duration")}
                    value={editDraft.demoActiveDays}
                    onChange={(e) => handleDraftChange("demoActiveDays", e.target.value)}
                    inputProps={{ min: 0 }}
                    sx={{ width: "100%", minWidth: 0 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    InputLabelProps={{ shrink: true }} label={editorText("Market runs / day")}
                    value={editDraft.demoMarketOptimizationLimitPerDay}
                    onChange={(e) =>
                      handleDraftChange("demoMarketOptimizationLimitPerDay", e.target.value)
                    }
                    inputProps={{ min: 0 }}
                    sx={{ width: "100%", minWidth: 0 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    InputLabelProps={{ shrink: true }} label={editorText("Feasibility runs / day")}
                    value={editDraft.demoFeasibilityLimitPerDay}
                    onChange={(e) =>
                      handleDraftChange("demoFeasibilityLimitPerDay", e.target.value)
                    }
                    inputProps={{ min: 0 }}
                    sx={{ width: "100%", minWidth: 0 }}
                  />
                </Box>
                <Stack
                  sx={{
                    ...editorFieldsSx,
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(15,23,42,0.03)",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {editorText("Demo expires")}: <strong>{editorText(formatDemoExpiryCell(editDraft))}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {editorText("Configured duration")}: <strong>{editDraft.demoActiveDays} {editorText("days")}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {editorText("Remaining days")}: <strong>{editDraft.demoRemainingDays}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {editorText("Remaining runs today")}:{" "}
                    <strong>
                      {editorText("Market")} {editDraft.marketOptimizationRemainingToday}, {editorText("Feasibility")}{" "}
                      {editDraft.feasibilityRemainingToday}
                    </strong>
                  </Typography>
                </Stack>
                {!editDraft.demoStartedAt && editDraft.demoModeEnabled ? (
                  <Typography variant="caption" color="warning.main">
                    {editorText("If the backend supports it, the demo start date can be auto-filled on save.")}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1, "& .MuiButton-root": { textTransform: "none" } }}>
          <Button onClick={handleCloseEditor} disabled={Boolean(savingUserId)}>
            {editorText("Cancel")}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={Boolean(savingUserId)}>
            {editorText(savingUserId === editingUserId ? "Saving..." : "Save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          severity={snack.severity}
          icon={snack.icon}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
